import { useSyncedHealth } from '../sync/HealthConnection';
import { DataSourceLabel } from '../../components/ui/DataSourceLabel';
import { EmptyState } from '../../components/ui/EmptyState';
import { MetricCard } from '../../components/ui/MetricCard';
import { MetricGrid } from '../../components/ui/MetricGrid';
import { PageHeader } from '../../components/ui/PageHeader';
import { SectionHeader } from '../../components/ui/SectionHeader';
import { StatusBadge, type BadgeTone } from '../../components/ui/StatusBadge';
import { conditions, cycleRecords, healthSyncStatus, latestMetrics, painEntries, recoveryPlan } from '../../mock/bodyData';
import type { HealthMetric } from '../../types/health';
import './HealthPage.css';

const healthSections = [
  { label: 'Conditions', value: String(conditions.filter((condition) => condition.type === 'condition').length || '—') },
  { label: 'Injuries', value: String(conditions.filter((condition) => condition.type === 'injury').length) },
  { label: 'Pain entries', value: String(painEntries.length) },
  { label: 'Medications', value: '—' },
  { label: 'Supplements', value: '—' },
  { label: 'Appointments', value: '—' },
  { label: 'Documents', value: '—' },
];

const metricLabels: Partial<Record<HealthMetric['type'], string>> = {
  weight: 'Weight',
  body_fat: 'Body fat',
  lean_body_mass: 'Lean mass',
  sleep_duration: 'Sleep',
  resting_heart_rate: 'Resting HR',
  hrv: 'HRV',
};

export function HealthPage() {
  const synced = useSyncedHealth();
  const condition = conditions[0];
  const pain = painEntries[0];
  const currentCycle = (synced.cycles.length ? synced.cycles.slice().sort((a, b) => b.date.localeCompare(a.date)) : cycleRecords).find((record) => !record.isEstimated);
  const appleHealthSync = healthSyncStatus.find((sync) => sync.id === 'sync_apple_health');
  const connectedMetrics = synced.metrics.length ? synced.metrics : latestMetrics.filter((metric) => appleHealthSync?.metricTypes.includes(metric.type));

  function syncTone(status: (typeof healthSyncStatus)[number]['status']): BadgeTone {
    if (status === 'connected') return 'good';
    if (status === 'manual') return 'primary';
    return 'neutral';
  }

  return (
    <div className="page-stack">
      <PageHeader eyebrow="HEALTH" title="Health" subtitle="Personal health organization, not a hospital EMR." />

      <MetricGrid>
        <MetricCard label="Apple Health" value={synced.metrics.length ? '1' : appleHealthSync ? String(appleHealthSync.metricTypes.length) : '—'} metadata="Connected metrics" />
        <MetricCard label="Active injury" value={condition ? '1' : '—'} metadata={condition?.name ?? 'No active injury'} />
        <MetricCard label="Pain today" value={pain ? `${pain.painScore}/10` : '—'} metadata={pain ? `${pain.side} ${pain.location}` : 'No entry'} />
        <MetricCard label="Cycle" value={currentCycle?.cycleDay ? `Day ${currentCycle.cycleDay}` : currentCycle ? currentCycle.flow ?? 'Unspecified' : '—'} metadata={currentCycle ? `Recorded ${currentCycle.date}` : 'No cycle record'}>
          {currentCycle ? <DataSourceLabel source={currentCycle.source} /> : null}
        </MetricCard>
      </MetricGrid>

      <section>
        <SectionHeader title="Health sync" subtitle="Body keeps source-aware Apple Health samples beside manual health logs." />
        <div className="sync-grid">
          {healthSyncStatus.map((sync) => (
            <article className="sync-card" key={sync.id}>
              <div>
                <h3>{sync.label}</h3>
                <p>{sync.id === 'sync_apple_health' && synced.count ? 'Private records received from your iPhone.' : sync.note}</p>
              </div>
              <StatusBadge tone={syncTone(sync.status)}>{sync.id === 'sync_apple_health' && synced.count ? 'records received' : sync.status}</StatusBadge>
              <span>{sync.lastSync ? `Last update ${sync.lastSync.slice(5, 16).replace('T', ' ')}` : 'Not connected yet'}</span>
            </article>
          ))}
        </div>
      </section>

      {synced.cycles.length > 0 ? <section><SectionHeader title="Menstrual flow records" subtitle="Recorded flow only; no period prediction." />
        <div className="health-metric-list">{synced.cycles.slice().sort((a, b) => b.date.localeCompare(a.date)).map(record => <article className="health-metric-row" key={record.id}>
          <span>{record.date}</span><strong>{record.flow ?? 'Unspecified'}</strong><DataSourceLabel source={record.source} />
        </article>)}</div>
      </section> : null}
      <section>
        <SectionHeader title="Health Areas" />
        <div className="health-section-grid">
          {healthSections.map((section) => (
            <MetricCard key={section.label} label={section.label} value={section.value} metadata={section.value === '—' ? 'Not tracked yet' : 'Tracked'} />
          ))}
        </div>
      </section>

      <section>
        <SectionHeader title="Body metric context" subtitle="Apple Health metrics that can influence health and recovery decisions." />
        <div className="health-metric-list">
          {connectedMetrics.map((metric) => (
            <article className="health-metric-row" key={metric.id}>
              <div>
                <h3>{metricLabels[metric.type] ?? metric.type}</h3>
                <p>{metric.startDate.slice(0, 10)}</p>
              </div>
              <strong className="numeric">
                {metric.value} {metric.unit}
              </strong>
              <DataSourceLabel source={metric.source} />
            </article>
          ))}
        </div>
      </section>

      <section>
        <SectionHeader title="Injury / Pain Architecture" subtitle="Timeline-ready model for injury, surgery, rehabilitation, and recovery." />
        <article className="condition-card">
          <div>
            <h3>{condition.name}</h3>
            <p>{condition.notes}</p>
          </div>
          <div className="condition-meta">
            <StatusBadge tone="warning">{condition.status}</StatusBadge>
            <span>{condition.side} side</span>
            <span>Started {condition.startDate}</span>
          </div>
        </article>
        <article className="condition-card">
          <div>
            <h3>Recovery guidance</h3>
            <p>{recoveryPlan.recommendation}</p>
          </div>
          <div className="condition-meta">
            <StatusBadge tone="primary">{recoveryPlan.statusLabel}</StatusBadge>
            <span>Impact {recoveryPlan.readinessImpact}</span>
            <span>Check in {recoveryPlan.nextCheckIn}</span>
          </div>
        </article>
        <article className="condition-card">
          <div>
            <h3>Pain entry</h3>
            <p className="numeric">
              {pain.location} · {pain.side} · {pain.painScore}/10
            </p>
            {pain.notes ? <p>{pain.notes}</p> : null}
          </div>
          <div className="condition-meta">
            <StatusBadge tone="neutral">{pain.painType}</StatusBadge>
            <span>{pain.source}</span>
          </div>
        </article>
      </section>

      <EmptyState title="Documents are not connected yet" description="Future records can attach files without turning Body into a full medical record system." />
    </div>
  );
}
